import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { confirmSignUp, fetchAuthSession, resendSignUpCode, signIn, signUp } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { apiFetch } from '@/services/apiGateway';
import { GlobalHeader } from '@/components/GlobalHeader';
import { CURRENT_CONSENT_VERSION } from '@/types/api';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { getRegionI18nKey } from '@/utils/region';
import i18n from '@/i18n';

function friendlyError(err: unknown): string {
  const msg = err instanceof Error ? err.message : '';
  const t = i18n.t.bind(i18n);
  if (msg.includes('password') || msg.includes('Password')) return t('register_parent.err_password');
  if (msg.includes('UsernameExistsException') || msg.includes('already')) return t('register_parent.err_exists');
  if (msg.includes('CodeMismatchException') || msg.includes('Invalid verification')) return t('register_parent.err_code_mismatch');
  if (msg.includes('ExpiredCodeException')) return t('register_parent.err_code_expired');
  return msg || t('register_parent.err_generic');
}

export function RegisterParentPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const init = useAuthStore((s) => s.init);

  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  // Step 2
  const [otpCode, setOtpCode]   = useState('');
  // Step 3
  const [displayName, setDisplayName]   = useState('');
  const [consentAi, setConsentAi]       = useState(false);
  const [consentCurriculum, setConsentCurriculum] = useState(false);

  const [isLoading, setIsLoading]     = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  // Set whenever this page already has a confirmed Cognito session (either
  // found on mount, or established by the account-recovery branch below) —
  // handleProfile must not call signIn() again in that case.
  const [skipSignIn, setSkipSignIn] = useState(false);

  // ── Recover an already-signed-in user with no backend profile ──────────────
  // A parent can land here (or on this route directly) already authenticated
  // but with no Parent record — e.g. POST /parents never completed earlier,
  // and every other page assumes a profile exists. Detect that on mount and
  // send them straight to step 3 instead of requiring them to re-enter
  // credentials on a page that's normally for brand-new sign-ups.
  useEffect(() => {
    (async () => {
      try {
        const session = await fetchAuthSession();
        if (!session.tokens?.idToken) return;
      } catch {
        return;
      }
      setSkipSignIn(true);
      try {
        await apiFetch('/parents/me');
        await init();
        navigate('/parent', { replace: true });
      } catch (profileErr) {
        if (profileErr instanceof Error && profileErr.message.includes('404')) {
          setStep(3);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Step 1: Cognito sign up (no name yet) ───────────────────────────────────
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await signUp({ username: email, password });
      setStep(2);
    } catch (err) {
      const name = err instanceof Error ? (err as { name?: string }).name : undefined;
      if (name === 'UsernameExistsException') {
        // Account exists but may still be UNCONFIRMED (e.g. the original code
        // was never received/entered). resendSignUpCode succeeds only for an
        // unconfirmed account, so use it to disambiguate and, when it works,
        // send the user straight back into the OTP step with a fresh code.
        try {
          await resendSignUpCode({ username: email });
          setResendMessage(t('register_parent.code_resent'));
          setStep(2);
        } catch {
          // Already confirmed. That still leaves a second stuck state: the
          // Cognito account is fully confirmed but the backend Parent profile
          // (POST /parents, step 3) was never created — e.g. it failed
          // earlier due to an unrelated API outage. Sign in with the entered
          // password to check which case this is, since there's otherwise no
          // route back into step 3 for an already-confirmed account.
          try {
            await signIn({ username: email, password });
            setSkipSignIn(true);
            try {
              await apiFetch('/parents/me');
              // Profile already exists — this is just a returning user.
              await init();
              navigate('/parent', { replace: true });
            } catch (profileErr) {
              if (profileErr instanceof Error && profileErr.message.includes('404')) {
                setStep(3);
              } else {
                setError(friendlyError(err));
              }
            }
          } catch {
            setError(friendlyError(err));
          }
        }
      } else {
        setError(friendlyError(err));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── Step 2: confirm OTP ─────────────────────────────────────────────────────
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await confirmSignUp({ username: email, confirmationCode: otpCode });
      setStep(3);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setResendMessage(null);
    setIsResending(true);
    try {
      await resendSignUpCode({ username: email });
      setResendMessage(t('register_parent.code_resent'));
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setIsResending(false);
    }
  };

  // ── Step 3: sign in → POST /parents → init → /parent ───────────────────────
  const handleProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim()) return;
    setError(null);
    setIsLoading(true);
    try {
      if (!skipSignIn) {
        try {
          await signIn({ username: email, password });
        } catch (signInErr) {
          // Belt-and-braces: an active session (from either recovery path
          // above) makes a second signIn() throw rather than just fail.
          const signInErrName = signInErr instanceof Error ? (signInErr as { name?: string }).name : undefined;
          if (signInErrName !== 'UserAlreadyAuthenticatedException') throw signInErr;
        }
      }
      await apiFetch('/parents', {
        method: 'POST',
        body: JSON.stringify({
          display_name: displayName.trim(),
          consent_account: true,
          consent_ai: consentAi,
          consent_curriculum: consentCurriculum,
          consent_version: CURRENT_CONSENT_VERSION,
        }),
      });
      await init();
      navigate('/parent', { replace: true });
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 3) { setStep(2); return; }
    if (step === 2) { setStep(1); return; }
    navigate('/login');
  };

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader showBack onBack={handleBack} customTitle={t('register_parent.title')} />
        <div className="app-content" style={{ paddingBottom: '24px' }}>

          {/* ── Step progress bar ── */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '24px' }}>
            {([1, 2, 3] as const).map((s) => (
              <div
                key={s}
                style={{
                  flex: 1, height: '4px', borderRadius: '2px',
                  background: step >= s ? 'var(--primary)' : 'var(--border)',
                  transition: 'background 0.2s',
                }}
              />
            ))}
          </div>

          {/* ── Step 1: Email + Password ── */}
          {step === 1 && (
            <>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 6px' }}>
                {t('register_parent.title')}
              </h1>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', margin: '0 0 24px', lineHeight: 1.5 }}>
                {t('register_parent.step1_desc')}
              </p>

              <div className="card">
                <form onSubmit={handleSignUp} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div className="form-group">
                    <label htmlFor="email">{t('register_parent.email_address_label')}</label>
                    <input
                      id="email"
                      type="email"
                      className="input"
                      placeholder={t('auth.email_placeholder')}
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </div>

                  <PasswordInput
                    id="password"
                    label={t('auth.password_label')}
                    placeholder={t('register_parent.password_placeholder')}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    required
                  />

                  {error && (
                    <p style={{
                      color: 'var(--danger)', fontSize: '0.85rem', margin: 0,
                      padding: '10px 14px', background: '#FEF2F2', borderRadius: '8px', lineHeight: 1.5,
                    }}>
                      ⚠️ {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading || !email.trim() || !password}
                    style={{ width: '100%', marginTop: '4px' }}
                  >
                    {isLoading ? t('register_parent.creating_account') : t('register_parent.create_account_btn')}
                  </button>
                </form>
              </div>
            </>
          )}

          {/* ── Step 2: OTP verification ── */}
          {step === 2 && (
            <>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 6px' }}>
                {t('register_parent.check_email_heading')}
              </h1>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', margin: '0 0 32px', lineHeight: 1.5 }}>
                {t('register.code_sent')} <strong>{email}</strong>.
              </p>

              <form onSubmit={handleVerify} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  className="input"
                  placeholder="000000"
                  value={otpCode}
                  onChange={(e) => {
                    setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                    setError(null);
                    setResendMessage(null);
                  }}
                  disabled={isLoading}
                  style={{
                    fontSize: '2rem', letterSpacing: '4px',
                    textAlign: 'center', padding: '16px', fontWeight: 700,
                  }}
                  autoFocus
                />

                {error && (
                  <p style={{
                    color: 'var(--danger)', fontSize: '0.85rem', margin: 0,
                    padding: '10px 14px', background: '#FEF2F2', borderRadius: '8px', lineHeight: 1.5,
                  }}>
                    ⚠️ {error}
                  </p>
                )}

                {resendMessage && !error && (
                  <p style={{
                    color: 'var(--success, #059669)', fontSize: '0.85rem', margin: 0,
                    padding: '10px 14px', background: '#ECFDF5', borderRadius: '8px', lineHeight: 1.5,
                  }}>
                    ✓ {resendMessage}
                  </p>
                )}

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading || otpCode.length < 6}
                  style={{ width: '100%' }}
                >
                  {isLoading ? t('register_parent.verifying') : t('register_parent.verify_continue_btn')}
                </button>

                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={handleResend}
                  disabled={isLoading || isResending}
                  style={{ width: '100%', fontSize: '0.875rem' }}
                >
                  {isResending ? t('register_parent.resending') : t('auth.resend_code')}
                </button>
              </form>
            </>
          )}

          {/* ── Step 3: Complete profile ── */}
          {step === 3 && (
            <>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 6px' }}>
                {t('register_parent.complete_profile_heading')}
              </h1>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', margin: '0 0 24px', lineHeight: 1.5 }}>
                {t('register_parent.step3_desc')}
              </p>

              <div className="card">
                <form onSubmit={handleProfile} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div className="form-group">
                    <label htmlFor="displayName">{t('register_parent.first_name_label')}</label>
                    <input
                      id="displayName"
                      type="text"
                      className="input"
                      placeholder={t('register_parent.name_placeholder')}
                      autoComplete="given-name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </div>

                  <div style={{ background: 'var(--bg-light)', borderRadius: '10px', padding: '16px' }}>
                    <p style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-dark)', margin: '0 0 12px' }}>
                      {t('register.privacy_choices')}
                    </p>

                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '10px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={consentAi}
                        onChange={(e) => setConsentAi(e.target.checked)}
                        style={{ marginTop: '2px', flexShrink: 0 }}
                      />
                      <span style={{ fontSize: '0.825rem', color: 'var(--text-dark)', lineHeight: 1.5 }}>
                        {t('register.consent_ai_label')}
                      </span>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={consentCurriculum}
                        onChange={(e) => setConsentCurriculum(e.target.checked)}
                        style={{ marginTop: '2px', flexShrink: 0 }}
                      />
                      <span style={{ fontSize: '0.825rem', color: 'var(--text-dark)', lineHeight: 1.5 }}>
                        {t('register.consent_curriculum_label')}
                      </span>
                    </label>

                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '12px 0 0', lineHeight: 1.5 }}>
                      {t('register.tos_prefix')} <strong>{t('register.tos_terms')}</strong> {t('register.tos_suffix', { region: t(getRegionI18nKey()) })}
                    </p>
                  </div>

                  {error && (
                    <p style={{
                      color: 'var(--danger)', fontSize: '0.85rem', margin: 0,
                      padding: '10px 14px', background: '#FEF2F2', borderRadius: '8px', lineHeight: 1.5,
                    }}>
                      ⚠️ {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading || !displayName.trim()}
                    style={{ width: '100%', marginTop: '4px' }}
                  >
                    {isLoading ? t('register_parent.setting_up') : t('register_parent.go_dashboard_btn')}
                  </button>
                </form>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
